/**
 * T-Shaped Floor Renderer
 *
 * Creates Three.js geometry for T-shaped floor surfaces.
 * Supports south-facing and north-facing orientations.
 *
 * South-facing T-shape (door at +Z / south):
 *
 *    +--------------+     wing bay (unit_width wide, wing_depth deep)
 *    |              |
 *    +--+        +--+
 *       |        |        tail bay (tail_width wide, wing_depth deep)
 *       +--------+
 *
 * North-facing: mirrored vertically (door at -Z / north)
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import { createFloorMaterial, applyShadowSettings } from '../utils/materials'

export interface TShapeDimensions {
  /** Wing bay width (full width of T-shape) in feet */
  unitWidth: number
  /** Wing bay depth in feet */
  wingDepth: number
  /** Tail bay width in feet */
  tailWidth: number
  /** Tail bay depth in feet (defaults to wingDepth for backward compat) */
  tailDepth?: number
}

export interface TShapedFloorOptions {
  color?: string
  enableShadows?: boolean
  yOffset?: number
}

/**
 * Create a THREE.Shape for a T-shaped floor plan.
 *
 * The shape is centered on its bounding box center (unitWidth × 2*wingDepth).
 *
 * @param dims - T-shape dimensions in feet
 * @param facing - 'south' = door at +Z, 'north' = door at -Z
 */
export function createTShape(
  dims: TShapeDimensions,
  facing: 'south' | 'north'
): THREE.Shape {
  const { unitWidth, wingDepth, tailWidth } = dims
  const td = dims.tailDepth ?? wingDepth
  const stepWidth = (unitWidth - tailWidth) / 2
  const totalDepth = wingDepth + td

  // Center offsets
  const halfWidth = unitWidth / 2
  const halfDepth = totalDepth / 2

  const shape = new THREE.Shape()

  if (facing === 'south') {
    // Door at +Y (will become +Z after rotation)
    // Start at top-left of wing bay, go clockwise
    //
    //  (-hw, +hd)-----(+hw, +hd)     ← wing top (door side)
    //       |                 |
    //  (-hw, +hd-wd)--+      +--(+hw, +hd-wd)
    //                 |      |
    //                 |      |
    //  (-hw+sw, -hd)--+------+--(+hw-sw, -hd)   ← tail bottom (back side)
    //
    // where hw=halfWidth, hd=halfDepth, wd=wingDepth, sw=stepWidth

    shape.moveTo(-halfWidth, halfDepth)                       // top-left (wing)
    shape.lineTo(halfWidth, halfDepth)                        // top-right (wing)
    shape.lineTo(halfWidth, halfDepth - wingDepth)            // right step top
    shape.lineTo(halfWidth - stepWidth, halfDepth - wingDepth) // right step corner
    shape.lineTo(halfWidth - stepWidth, -halfDepth)           // tail bottom-right
    shape.lineTo(-halfWidth + stepWidth, -halfDepth)          // tail bottom-left
    shape.lineTo(-halfWidth + stepWidth, halfDepth - wingDepth) // left step corner
    shape.lineTo(-halfWidth, halfDepth - wingDepth)           // left step top
    shape.closePath()
  } else {
    // North-facing: door at -Y (will become -Z after rotation)
    // Mirror vertically: wing bay is at bottom, tail at top
    shape.moveTo(-halfWidth, -halfDepth)                      // bottom-left (wing/door)
    shape.lineTo(halfWidth, -halfDepth)                       // bottom-right (wing/door)
    shape.lineTo(halfWidth, -halfDepth + wingDepth)           // right step bottom
    shape.lineTo(halfWidth - stepWidth, -halfDepth + wingDepth) // right step corner
    shape.lineTo(halfWidth - stepWidth, halfDepth)            // tail top-right
    shape.lineTo(-halfWidth + stepWidth, halfDepth)           // tail top-left
    shape.lineTo(-halfWidth + stepWidth, -halfDepth + wingDepth) // left step corner
    shape.lineTo(-halfWidth, -halfDepth + wingDepth)          // left step bottom
    shape.closePath()
  }

  return shape
}

/**
 * Render a T-shaped floor mesh
 *
 * @param dims - T-shape dimensions in feet
 * @param facing - Door orientation
 * @param options - Rendering options
 * @returns THREE.Mesh positioned horizontally
 */
export function renderTShapedFloor(
  dims: TShapeDimensions,
  facing: 'south' | 'north',
  options: TShapedFloorOptions = {}
): THREE.Mesh {
  const {
    color,
    enableShadows = true,
    yOffset = 0.01,
  } = options

  const shape = createTShape(dims, facing)
  const geometry = new THREE.ShapeGeometry(shape)

  // Scale from feet to meters
  geometry.scale(FEET_TO_METERS, FEET_TO_METERS, 1)

  const material = createFloorMaterial({ color })
  const mesh = new THREE.Mesh(geometry, material)

  // Rotate to horizontal (shape is on XY plane, we need it on XZ)
  mesh.rotation.x = -Math.PI / 2

  // Position slightly above ground to avoid z-fighting
  mesh.position.y = yOffset * FEET_TO_METERS

  applyShadowSettings(mesh, enableShadows, false)
  mesh.name = 'tshape-floor'

  return mesh
}
